/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import '../../styles/ckeditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (data: string) => void;
  placeholder?: string;
  height?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing your content here...',
  height = 300
}) => {
  // Start with blank content
  const defaultContent = value || '';
  const handleEditorReady = (editor: unknown) => {
    // Editor is ready to use
    console.log('Editor is ready to use!', editor);
    console.log('Available toolbar items:', (editor as any)?.ui?.componentFactory?.names());
    
    // Check if color plugins are available
    const editorInstance = editor as any;
    if (editorInstance?.plugins?.has('FontColor')) {
      console.log('FontColor plugin is available');
    } else {
      console.log('FontColor plugin is NOT available');
    }
    
    if (editorInstance?.plugins?.has('FontBackgroundColor')) {
      console.log('FontBackgroundColor plugin is available');
    } else {
      console.log('FontBackgroundColor plugin is NOT available');
    }
  };

  const handleEditorChange = (event: unknown, editor: { getData: () => string }) => {
    const data = editor.getData();
    onChange(data);
  };

  return (
    <div className="rich-text-editor" style={{ minHeight: `${height}px` }}>
      <CKEditor
        editor={ClassicEditor as any}
        data={defaultContent}
        onReady={handleEditorReady}
        onChange={handleEditorChange}
        config={{
           placeholder: placeholder,
           toolbar: [
             'heading',
             '|',
             'bold',
             'italic',
             'underline',
             'strikethrough',
             '|',
             'fontSize',
             'fontFamily',
             'fontColor',
             'fontBackgroundColor',
             '|',
             'bulletedList',
             'numberedList',
             '|',
             'alignment',
             '|',
             'link',
             'blockQuote',
             'insertTable',
             '|',
             'imageUpload',
             'mediaEmbed',
             '|',
             'undo',
             'redo',
             '|',
             'findAndReplace',
             'selectAll',
             '|',
             'horizontalLine',
             'specialCharacters',
             '|',
             'removeFormat',
             '|',
             'sourceEditing'
           ],
          heading: {
            options: [
              { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
              { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
              { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
              { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
              { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
              { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
              { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
            ]
          },
          fontSize: {
            options: [
              9, 11, 13, 'default', 17, 19, 21, 27, 35
            ],
            supportAllValues: true
          },
          fontFamily: {
            options: [
              'default',
              'Arial, Helvetica, sans-serif',
              'Courier New, Courier, monospace',
              'Georgia, serif',
              'Lucida Sans Unicode, Lucida Grande, sans-serif',
              'Tahoma, Geneva, sans-serif',
              'Times New Roman, Times, serif',
              'Trebuchet MS, Helvetica, sans-serif',
              'Verdana, Geneva, sans-serif'
            ],
            supportAllValues: true
          },
          fontColor: {
            colors: [
              { color: '#000000', label: 'Black' },
              { color: '#FF0000', label: 'Red' },
              { color: '#00FF00', label: 'Green' },
              { color: '#0000FF', label: 'Blue' },
              { color: '#FFFF00', label: 'Yellow' },
              { color: '#FF00FF', label: 'Magenta' },
              { color: '#00FFFF', label: 'Cyan' },
              { color: '#FFA500', label: 'Orange' },
              { color: '#800080', label: 'Purple' },
              { color: '#008000', label: 'Dark Green' },
              { color: '#FFC0CB', label: 'Pink' },
              { color: '#A52A2A', label: 'Brown' },
              { color: '#808080', label: 'Gray' },
              { color: '#C0C0C0', label: 'Silver' },
              { color: '#FFFFFF', label: 'White', hasBorder: true }
            ]
          },
          fontBackgroundColor: {
            colors: [
              { color: '#FF0000', label: 'Red' },
              { color: '#00FF00', label: 'Green' },
              { color: '#0000FF', label: 'Blue' },
              { color: '#FFFF00', label: 'Yellow' },
              { color: '#FF00FF', label: 'Magenta' },
              { color: '#00FFFF', label: 'Cyan' },
              { color: '#FFA500', label: 'Orange' },
              { color: '#800080', label: 'Purple' },
              { color: '#008000', label: 'Dark Green' },
              { color: '#FFC0CB', label: 'Pink' }
            ]
          },
          table: {
            contentToolbar: [
              'tableColumn',
              'tableRow',
              'mergeTableCells',
              'tableProperties',
              'tableCellProperties'
            ]
          },
          image: {
            toolbar: [
              'imageTextAlternative',
              'imageStyle:full',
              'imageStyle:side',
              '|',
              'imageResize'
            ]
          },
          link: {
            decorators: {
              openInNewTab: {
                mode: 'manual',
                label: 'Open in a new tab',
                attributes: {
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }
              }
            }
          }
        }}
      />
      
      {/* Character Counter */}
      <div className="mt-2 text-right text-sm" style={{ color: '#ede8c5' }}>
        {defaultContent.replace(/<[^>]*>/g, '').length} characters
      </div>
    </div>
  );
};

export default RichTextEditor;
